// فقرة «إعدادات الطباعة»: رفع شعار المركز وتعديله وحذفه، والتحكّم بظهوره في
// المطبوعات، مع معاينة تُحاكي رأس الإيصال الحراري وورقة A4 قبل الاعتماد.
//
// الشعار يُحفظ مركزياً في إعدادات النظام، فيسري تلقائياً على كل قوالب الطباعة
// (الدخول، الخروج والتسوية، فواتير نقاط البيع، الاشتراكات، تعبئة البطاقات،
// كشوف الحسابات والتقارير) بلا تعديل أي إيصال على حدة.
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { useAuth } from '../../../../shared/useAuth.js';
import { toast } from '../../../../shared/useToast.js';
import {
  useSettings,
  useUpdateSettings,
  useUploadPrintLogo,
  useDeletePrintLogo
} from '../../../../shared/useSettings.js';

const ACCEPTED = ['image/png', 'image/jpeg'];
const MAX_BYTES = 5 * 1024 * 1024;

function Toggle({ checked, onChange, disabled, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={clsx(
        'relative w-9 h-5 rounded-full transition-colors flex-none',
        checked ? 'bg-orange' : 'bg-[#E5E0D5]',
        disabled && 'opacity-60'
      )}
    >
      <span
        className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow"
        style={{ insetInlineStart: checked ? 18 : 2, transition: 'inset-inline-start .15s' }}
      />
    </button>
  );
}

export function PrintSettingsCard() {
  const { data, isLoading } = useSettings();
  const updateMut = useUpdateSettings();
  const uploadMut = useUploadPrintLogo();
  const deleteMut = useDeletePrintLogo();
  const company = useAuth((s) => s.company);
  const fileRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const logoUrl = String(data?.printLogoUrl || '');
  const enabled = data?.printLogoEnabled !== false;
  const brand = String(data?.businessName || '').trim() || (company && company.name) || 'Kids Zone';

  // معاينة الملف المختار قبل الرفع (blob محلي) — تُحرَّر عند تغييره.
  useEffect(() => () => {
    if (pending && pending.preview) URL.revokeObjectURL(pending.preview);
  }, [pending]);

  function onPick(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error('صيغة الشعار غير مدعومة — المسموح: PNG أو JPG أو JPEG');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('حجم الشعار كبير جداً — الحدّ الأعلى 5 ميغابايت');
      return;
    }
    if (pending && pending.preview) URL.revokeObjectURL(pending.preview);
    setPending({ file, preview: URL.createObjectURL(file) });
  }

  function save() {
    if (!pending) return;
    uploadMut.mutate(pending.file, {
      onSuccess: () => {
        if (pending.preview) URL.revokeObjectURL(pending.preview);
        setPending(null);
      }
    });
  }

  function cancelPending() {
    if (pending && pending.preview) URL.revokeObjectURL(pending.preview);
    setPending(null);
  }

  const shownLogo = pending ? pending.preview : (logoUrl || null);
  const busy = isLoading || uploadMut.isPending || deleteMut.isPending;

  return (
    <Card header="إعدادات الطباعة">
      <div className="text-ink-3 text-[12px] mb-3 leading-relaxed">
        شعار المركز يظهر في أعلى منتصف كل المطبوعات: إيصالات الدخول والخروج والتسوية،
        فواتير نقاط البيع، إيصالات الاشتراكات الشهرية وتعبئة البطاقات، وكشوف الحسابات
        والتقارير. تُضبط أبعاده تلقائياً حسب الورق (طابعة حرارية 80مم أو A4) مع
        المحافظة على نسبة أبعاد الصورة. الصيغ المدعومة: PNG أو JPG أو JPEG، وبحدّ
        5 ميغابايت. إن لم يُضَف شعار أو أُطفئ خيار إظهاره تُطبع المستندات كما هي
        تماماً بلا مساحة فارغة.
      </div>

      <div className="flex gap-4 flex-wrap items-start">
        {/* الشعار الحالي / المختار */}
        <div style={{ width: 240 }}>
          <div className="text-[11.5px] font-semibold text-ink-2 mb-1">شعار الطباعة</div>
          <div
            className="rounded-[10px] border border-line bg-white grid place-items-center p-2"
            style={{ height: 130 }}
          >
            {shownLogo ? (
              <img
                src={shownLogo}
                alt="شعار الطباعة"
                style={{ maxWidth: '100%', maxHeight: 112, width: 'auto', height: 'auto', objectFit: 'contain' }}
              />
            ) : (
              <div className="text-center text-ink-3 text-[11.5px] flex flex-col items-center gap-1">
                <Icon name="print" size={22} color="#D6CCB3" />
                لا يوجد شعار — تُطبع المستندات بالاسم وحده
              </div>
            )}
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,.png,.jpg,.jpeg"
            className="hidden"
            onChange={onPick}
          />
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button
              type="button"
              className="btn"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
            >
              <Icon name="plus" size={13} /> {logoUrl || pending ? 'تغيير الشعار' : 'رفع شعار'}
            </button>
            {pending ? (
              <>
                <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>
                  <Icon name="check" size={13} />
                  {uploadMut.isPending ? 'جاري الحفظ...' : 'حفظ الشعار'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={cancelPending} disabled={busy}>
                  إلغاء
                </button>
              </>
            ) : null}
            {logoUrl && !pending ? (
              confirmDelete ? (
                <>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => {
                      deleteMut.mutate(undefined, { onSuccess: () => setConfirmDelete(false) });
                    }}
                    disabled={busy}
                  >
                    <Icon name="trash" size={13} />
                    {deleteMut.isPending ? 'جاري الحذف...' : 'تأكيد الحذف'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setConfirmDelete(false)}
                    disabled={busy}
                  >
                    تراجع
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-ghost text-red"
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy}
                >
                  <Icon name="trash" size={13} /> حذف الشعار
                </button>
              )
            ) : null}
          </div>
          {pending ? (
            <div className="text-[11px] text-orange-deep mt-1.5">
              معاينة قبل الحفظ — لن يظهر في المطبوعات حتى تضغط «حفظ الشعار».
            </div>
          ) : null}
        </div>

        {/* معاينة شكل الظهور على الورق */}
        <div className="flex-1 min-w-[280px]">
          <div className="text-[11.5px] font-semibold text-ink-2 mb-1">
            معاينة الظهور في المطبوعات
          </div>
          <div className="flex gap-3 flex-wrap">
            {/* إيصال حراري 80مم */}
            <div className="rounded-[10px] border border-line bg-white p-2.5" style={{ width: 190 }}>
              <div className="text-[10px] text-ink-3 mb-1.5 text-center">إيصال حراري 80مم</div>
              <div
                className="bg-white mx-auto"
                style={{ border: '1px dashed #D6CCB3', padding: 8, color: '#000' }}
              >
                <div style={{ textAlign: 'center' }}>
                  {shownLogo && enabled ? (
                    <img
                      src={shownLogo}
                      alt=""
                      style={{
                        maxWidth: 110, maxHeight: 44, width: 'auto', height: 'auto',
                        objectFit: 'contain', margin: '0 auto 3px', display: 'block'
                      }}
                    />
                  ) : null}
                  <div style={{ fontSize: 12, fontWeight: 800 }}>{brand}</div>
                  <div style={{ fontSize: 9 }}>إيصال تسوية وخروج</div>
                </div>
                <div style={{ borderTop: '1px dashed #000', margin: '6px 0' }} />
                <div style={{ fontSize: 9, display: 'flex', justifyContent: 'space-between' }}>
                  <span>الإجمالي</span><b>12,000 د.ع</b>
                </div>
              </div>
            </div>

            {/* ورقة A4 */}
            <div className="rounded-[10px] border border-line bg-white p-2.5" style={{ width: 210 }}>
              <div className="text-[10px] text-ink-3 mb-1.5 text-center">ورقة A4</div>
              <div
                className="bg-white"
                style={{ border: '1px solid #E5E0D5', padding: 10, color: '#000', aspectRatio: '1 / 1.414' }}
              >
                <div style={{ textAlign: 'center' }}>
                  {shownLogo && enabled ? (
                    <img
                      src={shownLogo}
                      alt=""
                      style={{
                        maxWidth: 120, maxHeight: 40, width: 'auto', height: 'auto',
                        objectFit: 'contain', margin: '0 auto 5px', display: 'block'
                      }}
                    />
                  ) : null}
                  <div style={{ fontSize: 11, fontWeight: 800 }}>كشف حساب بطاقة</div>
                  <div style={{ fontSize: 8, marginTop: 2 }}>2026-10-27</div>
                </div>
                <div style={{ marginTop: 8 }}>
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      style={{ height: 6, background: '#F3F0E8', marginBottom: 4, borderRadius: 2 }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          {!enabled ? (
            <div className="text-[11px] text-ink-3 mt-1.5">
              الخيار مُطفأ حالياً — المعاينة تُظهر المطبوعات بلا شعار، والملف محفوظ كما هو.
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex items-start gap-2.5 p-2.5 rounded-[10px] border border-line mt-3">
        <Toggle
          testId="print-logo-toggle"
          checked={enabled}
          disabled={busy || updateMut.isPending}
          onChange={(v) => updateMut.mutate({ printLogoEnabled: v })}
        />
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold">إظهار الشعار في الطباعة</div>
          <div className="text-[11.5px] text-ink-3 leading-relaxed">
            أطفئه لإيقاف ظهور الشعار في كل المطبوعات دون حذف الملف، وأعِد تشغيله في أي وقت.
          </div>
        </div>
      </div>
    </Card>
  );
}
